import Phaser from 'phaser';
import { GameConfig } from '../utils/GameConfig';

export class Roulette {
	private scene: Phaser.Scene;
	private wheelContainer: Phaser.GameObjects.Container;
	private wheelTween!: Phaser.Tweens.Tween;
	private totalNumbers = 37; // Números de 0 a 36
	private wheelRadius: number;
	private finalNumber: number = 0; // Número final do highlightNumbers
	private isSpinning: boolean = false;
	private highlightedSegment!: Phaser.GameObjects.Graphics | null; // Gráfico de destaque

	private spinnerW: Phaser.GameObjects.Image | null;
	private sticksS: Phaser.GameObjects.Image | null;
	private sticks: Phaser.GameObjects.Image | null;
	private boardCenter: Phaser.GameObjects.Image | null;

	constructor(scene: Phaser.Scene, x: number, y: number, wheelRadius: number) {
		this.scene = scene;
		this.wheelRadius = wheelRadius;
		this.highlightedSegment = null; // Inicialmente não há destaque
		this.wheelContainer = this.scene.add.container(x, y);

		// -----
		this.spinnerW = null;
		this.sticksS = null;
		this.sticks = null;
		this.boardCenter = null;
		// -----

		this.drawRoulette();
		this.addCenterCircle(); // Chamar função para adicionar o círculo no centro
		this.setInitialPositionByNumber(0);
	}

	// PRIVATE

	// Função para desenhar a roleta e os números
	private drawRoulette() {
		this.spinnerW = this.scene.add.image(0, 0, 'roulette').setOrigin(0.5).setScale(0.55).setInteractive();

		this.scene.add.triangle(252, 185, 0, 200, 200, 200, 100, 0, 0x000000).setAlpha(0.3).setScale(0.13).setRotation(3.14159).setDepth(10);
		this.scene.add.triangle(248, 183, 0, 200, 200, 200, 100, 0, 0xffffff).setScale(0.15).setRotation(3.14159).setDepth(10);
		this.scene.add.triangle(248, 181, 0, 200, 200, 200, 100, 0, 0x000060).setScale(0.1).setRotation(3.14159).setDepth(10);

		this.boardCenter = this.scene.add.image(0, 0, 'boardCenter').setScale(1.2).setOrigin(0.5).setDepth(12);
		// Create the sticksS image at position (610, 290) with origin at the center, set its alpha to 0.3, tint it to black, and scale it up to 120%
		this.sticksS = this.scene.add.image(10, 10, 'boardSticks').setAlpha(0.3).setTint(0x000000).setOrigin(0.5).setScale(1.5);
		// Create the sticks image at position (580, 260) with origin at the center and scale it up to 120%
		this.sticks = this.scene.add.image(0, 0, 'boardSticks').setOrigin(0.5).setScale(1.5);

		this.wheelContainer.add([this.spinnerW, this.boardCenter, this.sticksS, this.sticks]);
	}

	// Função para adicionar um círculo de borda branca no meio da roleta
	private addCenterCircle() {
		const centerCircle = this.scene.add.graphics();

		// Define o estilo da linha (borda branca) e desenha o círculo
		centerCircle.lineStyle(4, 0xa57c20); // Borda branca com espessura de 4 pixels
		centerCircle.strokeCircle(0, 0, this.wheelRadius * 0.55); // Círculo sem preenchimento, só borda

		// Adiciona o círculo ao container central
		this.wheelContainer.add(centerCircle);
	}

	// Calcula o ângulo baseado no número sorteado
	private getAngleForNumber(number: number): number {
		const degreesPerNumber = 360 / this.totalNumbers;
		return number * degreesPerNumber;
	}

	private getExactAngleForNumber(number: number): number {
		const entry = GameConfig.numang.find(n => n.n === number);
		if (!entry) {
			console.warn(`Ângulo não encontrado para o número ${number}. Usando 0 como fallback.`);
			return 0;
		}

		// Converte de radianos para graus (aqui o valor já parece em radianos)
		return Phaser.Math.RadToDeg(entry.a);
	}

	// PUBLIC

	// Inicia a rotação da roleta, juntamente com os números
	public startRouletteSpin(numang: number, num: number, callback?: () => void) {
		this.isSpinning = true;
		// Remove o gráfico de destaque anterior, se existir
		if (this.highlightedSegment) {
			this.highlightedSegment.destroy(); // Remove o destaque anterior da cena
			this.highlightedSegment = null; // Limpa a referência ao destaque anterior
		}

		// Tween contínuo para fazer a roleta e os números girarem indefinidamente
		this.wheelTween = this.scene.tweens.add({
			targets: [this.spinnerW, this.sticks, this.sticksS],
			angle: '+=' + numang,
			duration: 8000,
			ease: 'Sine.inOut',
			onComplete: () => {
				this.stopWheel(num);
				if (typeof callback === 'function' && callback) {
					callback();
				}
			}
		});
	}

	// Função para parar a roleta e destacar o número sorteado
	public stopWheel(finalNumber: number, immediateStop: boolean = false) {
		if (this.isSpinning) {
			this.isSpinning = false;
			this.finalNumber = finalNumber;

			// Calcular o ângulo final baseado no número sorteado
			const finalAngle = this.getAngleForNumber(finalNumber);

			if (immediateStop) {
				// Parar a roleta imediatamente
				console.log(`A roleta parou no número: ${this.finalNumber} :: 1`);
			} else {
				// Faz a roleta parar suavemente, juntamente com os números
				console.log(`A roleta parou no número: ${this.finalNumber} :: 2`);
			}
		}
	}

	// Adiciona uma imagem no centro da roleta com tamanho personalizado
	public addCenterImage(imageKey: string, width: number, height: number) {
		const centerImage = this.scene.add.image(0, 0, imageKey);
		centerImage.setDisplaySize(width, height); // Ajusta o tamanho da imagem de acordo com os parâmetros
		centerImage.setName('centerImage'); // Define um nome para a imagem para fácil acesso

		// Adiciona a imagem ao container central (ela ficará acima do destaque)
		this.wheelContainer.add(centerImage);
	}

	public randomIntFromInterval(min, max) {
		return Math.floor(Math.random() * (max - min + 1) + min);
	}

	public setInitialPositionByNumber(number: number) {
		const angle = this.getExactAngleForNumber(number);
		this.wheelContainer.setAngle(angle);
	}

	public toDegrees(angle) {
		return angle * (180 / Math.PI);
	}
}
